import { useAppContext } from "@qpr/client-api-base/context/AppCtxProvider";
import useLink from "@qpr/client-api-base/hooks/useLink";
import useElement from "backend/hooks/elements/useElement";
import PerformanceTrend from "components/PerformanceTrend";
import SplitView from "design/layouts/SplitView";
import StackView from "design/layouts/StackView";
import React from "react";
import Page from "design/pages/Page";
import MeasureInfo from "design/components/cards/MeasureInfo";
import PageContentMenu from "@qpr/client-api-base/uicomponents/view/PageContentMenu";
import { t } from "i18next";
import Anchor from "@qpr/client-api-base/ui/Anchor";
import ItemDrawer from "design/components/menus/ItemDrawer";
import useQueryBuilder from "@qpr/client-api-base/querybuilder/useQueryBuilder";
import defaultAttributes from "backend/defaultAttributes";
import { ICardElementWithChildren } from "backend/model/model";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import MeasureTable from "design/components/tables/MeasureTable";
import InitiativeProgressCard from "design/components/cards/InitiativeProgressCard/InitiativeProgressCard";
import HorizontalGrid from "design/components/grids/HorizontalGrid";
import SlippageCard from "design/components/cards/SlippageCard/SlippageCard";
import BudgetUtilizationCard from "design/components/cards/BudgetUtilizationCard/BudgetUtilizationCard";
import InitiativeHeading from "design/components/texts/InitiativeHeading";
import ProgressBar from "design/components/progress/ProgressBar";
import { makeThemedStyles } from "@qpr/client-api-base/viewcomponents/utils";
import LeftMenuPosition from "design/layouts/LeftMenuPosition";
import { formatDate, getDateFromMEAValue } from "utils/utils";
import AnalysisCard from "design/components/cards/AnalysisCard/AnalysisCard";
import { Series } from "design/components/charts/types";
import InitiativeToolbar from "components/toolbars/InitiativeToolbar";
import usePeriodLevels from "backend/hooks/model/usePeriodLevels";
import Todo from "design/components/misc/Todo";
import useComments from "backend/hooks/comments/useComments";
import UploadedFilesList from "@qpr/client-api-base/ui/components/UploadedFilesList";
import useAttachments from "backend/hooks/files/useAttachments";

const useStyles = makeThemedStyles("InitiativeDetails", (theme) => ({
  title: {
    fontSize: "1.828em",
    lineHeight: "1.5em",
    letterSpacing: "0.05625px",
    color: "rgba(63, 69, 79, 1)",
    margin: 0,
    marginBottom: "0.625em",
    fontWeight: 600,
  },
  table: {
    "& .MuiPaper-root": {
      all: "unset",
    },
    "& .MuiTableCell-stickyHeader": {
      backgroundColor: "unset",
    },
    "& .MuiTableCell-head": {
      textTransform: "uppercase",
      fontSize: "14px",
      color: "black",
    },
    "& .actProgCell": {
      borderInlineStart: "1px solid #e0e0e0",
    },
  },
}));

const SERIES: Series[] = [
  { key: "PLANNED_COMPLETION", name: "TARGET", color: "rgb(93,98,104)" },
  {
    key: "COMPLETION_PERCENTAGE",
    name: "ACTUAL",
    color: "rgba(38, 166, 242, 0.8)",
  },
];

const InitiativeDetails = () => {
  const styles = useStyles();
  const ctx = useAppContext();
  const link = useLink();
  const { data: comments } = useComments({
    id: ctx.elementId || "",
    period: ctx.period || "",
  });
  const { data: attachments } = useAttachments({
    elementId: ctx.elementId,
    period: ctx.period,
  });
  const [firstComment] = comments || [];
  const { data: periodlevels } = usePeriodLevels();

  const {
    data,
    isLoading,
    isFetching: isElementFetching,
    isRefetching,
  } = useElement(ctx.elementId);
  const {
    data: scorecardInitiatives,
    isLoading: isScorecardKpisLoading,
    isFetching,
  } = useQueryBuilder<ICardElementWithChildren[]>((q) =>
    q(ctx.elementId)
      .scorecard()
      .attributes(...defaultAttributes(ctx.period, []))
      .measures({ criteria: { typename: "Initiative" } })
  );
  const { isLoading: isChildrenLoading, data: children } = useHierarchy(
    ctx.elementId
  );
  const milestones = React.useMemo(() => {
    if (children) {
      return children.filter((c) => c.typename === "Milestone");
    }
    return [];
  }, [children]);

  // not sure why this is needed here...
  if (!data) {
    return null;
  }
  return (
    <Page
      isLoading={
        isLoading ||
        isScorecardKpisLoading ||
        isChildrenLoading ||
        isFetching ||
        isElementFetching ||
        isRefetching
      }
      Toolbar={InitiativeToolbar}
    >
      <SplitView left="180px" right="1100px" gap="20px">
        <div>
          <LeftMenuPosition>
            <ItemDrawer
              title="Initiatives"
              items={scorecardInitiatives?.data || []}
              onSelect={(item) => {
                link({ meaId: item.id });
              }}
            />
            <PageContentMenu
              componentData={{
                items: [
                  { title: t("Initiative Info"), anchor: "header" },
                  { title: t("Performance"), anchor: "performance" },
                  { title: t("Progress Trend"), anchor: "progress-trend" },
                  { title: t("Milestones"), anchor: "milestones" },
                  { title: t("Analysis"), anchor: "justification" },
                ],
              }}
            />
          </LeftMenuPosition>
        </div>
        <div>
          <StackView>
            <Anchor name="header" />
            <MeasureInfo
              title="Intitiative Information"
              items={[
                {
                  label: "Timeline",
                  text: `Start Date ${formatDate(
                    getDateFromMEAValue(data?.START_DATE),
                    // data?.START_DATE_prettyvalue || "",
                    "en-GB",
                    true
                  )}\nEnd Date ${formatDate(
                    getDateFromMEAValue(data?.END_DATE),
                    "en-GB",
                    true
                  )}`,
                },
                {
                  label: "Strategic Objective",
                  text:
                    (data?.parentTypename || "") === "Strategic Objective"
                      ? (data?.parentName || "") +
                        "\n" +
                        data?.parentDescription
                      : "",
                },
                {
                  label: "Description",
                  text: data?.description || "",
                },
                /*{
                  label: "Measurement Frequency",
                  text: data?.periodlevelname || "",
                },*/
              ]}
            />
            <Anchor name="performance" />
            <HorizontalGrid>
              <div style={{ height: "250px" }}>
                <InitiativeProgressCard
                  data={data}
                  series={[
                    {
                      title: "Planned Progress",
                      prettyvalue: "PLANNED_COMPLETION_prettyvalue",
                    },
                    { title: "Variance", prettyvalue: "VARIANCE_prettyvalue" },
                  ]}
                  display={{
                    title: data.COMPLETION_PERCENTAGE_prettyvalue,
                    value: "COMPLETION_PERCENTAGE",
                    colorKey: "rangecolor",
                    bgColorKey: "rangecolor",
                  }}
                />
              </div>
              <div style={{ height: "250px" }}>
                <SlippageCard
                  data={data}
                  dateDisplay={{
                    title: "Estimated End Date",
                    value: "ACTUAL_END_DATE",
                  }}
                  display={{
                    title: "Slippage",
                    unit: "SLIPPAGE_unit",
                    value: "SLIPPAGE",
                    valuewithoutsymbol: "SLIPPAGE_valuewithoutsymbol",
                    unitConversionFunction: (str: string) => {
                      const slippageUnits: Record<string, string> = {
                        d: "Days",
                        w: "Weeks",
                        y: "Years",
                      };
                      return slippageUnits[str] || str;
                    },
                  }}
                />
              </div>
              <div style={{ height: "250px" }}>
                <BudgetUtilizationCard
                  data={data}
                  display={{
                    colorKey: "rangecolor",
                    prettyvalue: "BUDGET_UTIL_prettyvalue",
                    value: "BUDGET_UTIL",
                  }}
                  series={[
                    {
                      title: "Total Budget",
                      prettyvalue: "BUDGET_prettyvalue",
                    },
                    {
                      title: "YTD Expenditure",
                      prettyvalue: "EXPENDITURE_prettyvalue",
                    },
                  ]}
                />
              </div>
            </HorizontalGrid>
            <div>
              <Anchor name="progress-trend" />
              <InitiativeHeading>Progress Trend</InitiativeHeading>

              <PerformanceTrend element={data} series={SERIES} width="1090px" />
            </div>

            <Anchor name="milestones" />

            <InitiativeHeading>Milestones</InitiativeHeading>
            <MeasureTable
              className={styles.table}
              items={milestones}
              columns={[
                {
                  title: "Period",
                  value: (row) => <div>{row.elem.period?.name || ""}</div>,
                },
                {
                  title: "Name",
                  value: (props) => <div>{props.elem.name}</div>,
                },
                {
                  title: "Actual Progress",
                  tdClassName: "actProgCell",
                  value: (props) =>
                    data.STATUS === "0" ? (
                      <div />
                    ) : (
                      <ProgressBar
                        barColor={`#${props.elem.rangecolor}`}
                        value={Number(props.elem.COMPLETION_PERCENTAGE) || 0}
                        title={
                          props.elem.COMPLETION_PERCENTAGE_prettyvalue ||
                          "0.00 %"
                        }
                        titleJustify="center"
                      />
                    ),
                },
                {
                  title: "Planned Progress",
                  value: (props) =>
                    data.STATUS === "0" ? (
                      <div />
                    ) : (
                      <ProgressBar
                        barColor={`#${props.elem.rangecolor}`}
                        value={Number(props.elem.PLANNED_COMPLETION) || 0}
                        title={
                          props.elem.PLANNED_COMPLETION_prettyvalue || "0.00 %"
                        }
                        titleJustify="center"
                      />
                    ),
                },
                {
                  title: "Variance",
                  value: (props) => (
                    <div>{props.elem.VARIANCE_prettyvalue}</div>
                  ),
                },
              ]}
            />

            <Anchor name="justification" />
            <InitiativeHeading subTitle={ctx.period}>
              Analysis
            </InitiativeHeading>
            {firstComment && data.STATUS !== "0" ? (
              <AnalysisCard
                userData={{
                  name: firstComment.modifiedby || firstComment.createdby,
                  subtitle: "Owner",
                }}
                date={new Date(
                  firstComment.lastmodifieddate
                ).toLocaleDateString()}
                description={
                  firstComment ? firstComment.description : "No Comments yet"
                }
              >
                <UploadedFilesList values={attachments} readOnly />
              </AnalysisCard>
            ) : (
              <AnalysisCard description="No Comments Yet">
                <UploadedFilesList values={attachments} readOnly />
              </AnalysisCard>
            )}

            <div style={{ height: "500px" }} />
          </StackView>
        </div>
      </SplitView>
    </Page>
  );
};

export default InitiativeDetails;
